import type { AreaId } from '@shared/brand';
import type { IncapacityKind } from './Incapacity';
import type { PermissionKind } from './PermissionRequest';
import type { FeatureKey, Permission, RejectionScope, Role } from '../vocabulary';
import type { AreaCatalog } from './AreaCatalog';

/**
 * Runtime configuration — the whole point of the OTA-safe design.
 *
 * Everything in this file is stored in Firestore and read through a live
 * listener, not compiled into the bundle. Turning off a module, editing a
 * rejection reason, changing the seniority table, granting a permission to a
 * role or rewording an email all take effect for every open session without a
 * redeploy.
 *
 * Two consequences the rest of the codebase depends on:
 *
 *  1. Nothing here may be treated as trusted for authorization. The permission
 *     matrix decides what the UI offers and is re-checked inside the privileged
 *     callables, but the security floor is the Firestore rules, which cannot be
 *     edited from the app. A configuration bug must not be able to widen access.
 *
 *  2. Every field needs a sane default. A client running an older build reads a
 *     document written by a newer one; unknown keys are preserved and missing
 *     keys fall back, so neither direction breaks.
 */

/** Company identity — the handoff's Configuración global › Identidad. */
export interface IdentityConfig {
  readonly commercialName: string;
  /**
   * Domains allowed to sign in. Authoritative for the blocking function; the
   * client copy only drives the `hd` hint and the login error text.
   */
  readonly allowedDomains: readonly string[];
  readonly hrEmail: string;
  /**
   * The address every notice goes out as — the prototype's `{{ mailFrom }}`.
   *
   * **Empty means unknown, and the screen has to say so.** Both places that showed
   * this used to build it from the domain (`vacaciones@${domain}`) and printed an
   * address that did not exist, while the real sender was whichever personal account
   * had authenticated the SMTP. With Gmail as the transport the extension's «Default
   * FROM» parameter is ignored outright, so this cannot be derived from anything the
   * portal knows: somebody has to write down what the mail actually goes out as, and
   * keep it in step with the extension. Guessing here is worse than a blank.
   */
  readonly mailFrom: string;
  readonly country: string;
  readonly logoPath: string | null;
}

export const DEFAULT_IDENTITY: IdentityConfig = {
  commercialName: 'Knotion',
  allowedDomains: ['knotion.com'],
  hrEmail: 'personas@knotion.com',
  // Deliberately blank rather than a plausible-looking default: an installation
  // that has not authenticated its SMTP yet has no sender to name.
  mailFrom: '',
  country: 'México',
  logoPath: null,
};

/** Scheduling and entitlement rules — Configuración global › Reglas y saldos. */
export interface RulesConfig {
  /**
   * Statutory minimum days per year, independent of the seniority table. Mexico's
   * LFT sets 12 for the first year; the table may grant more but never less.
   */
  readonly legalMinimumDays: number;
  /** Days of notice a request must give. The wizard warns below this. */
  readonly noticeDays: number;
  /** Fewest days a single request may cover. */
  readonly minimumRequestDays: number;
  /**
   * Meses, contados desde el aniversario en que abre cada periodo, durante los
   * que se pueden gozar sus días. Con más de 12 los periodos se enciman y pagan
   * primero los que vencen antes. Acotado a 12–36: ver `clampExpiryMonths`.
   */
  readonly expiryMonths: number;
  /**
   * Minutos de inactividad tras los cuales la sesión se cierra sola.
   *
   * Configurable y no constante porque el equilibrio entre seguridad y molestia
   * lo pone la empresa, no el código: media hora es razonable en una oficina
   * abierta y es un fastidio para quien captura altas toda la tarde. El decoder
   * lo acota entre 5 y 480 — un cero apagaría la medida sin que nadie se diera
   * cuenta de que la apagó.
   */
  readonly idleMinutes: number;
  /** Balance above which the reports screen paints a person's figure red. */
  readonly riskThresholdDays: number;
  /** Fallback cap for a sub area that has not set its own. */
  readonly defaultMaxConcurrentOut: number;
  /**
   * Días de aguinaldo. Piso legal 15 (art. 87); muchas empresas dan más.
   *
   * Configurable y no constante porque es un **mínimo**, no un valor: una empresa
   * que paga 30 días y lo tuviera fijo en 15 calcularía todos sus finiquitos por
   * la mitad de lo que debe.
   */
  readonly aguinaldoDays: number;
  /** Prima vacacional. Piso legal 0.25 (art. 80). */
  readonly primaVacacionalRate: number;
  /**
   * Salario mínimo general diario vigente, en centavos.
   *
   * Es el tope de la prima de antigüedad (art. 162) y **cambia cada 1 de enero**.
   * Codificarlo obligaría a un despliegue anual para no calcular mal, que es la
   * clase de dependencia que este portal ya evita con los feriados y la tabla de
   * antigüedad.
   */
  readonly minimumWageCents: number;
  readonly seniority: SeniorityConfig;
}

/**
 * The seniority table. `enabled` off means everyone gets `fixedDays` — the
 * handoff's "se puede apagar y usar días fijos".
 */
export interface SeniorityConfig {
  readonly enabled: boolean;
  readonly fixedDays: number;
  /**
   * Days the company grants on top of whichever bracket applies.
   *
   * The table itself is the law's — editing it would misstate the statutory
   * floor — so anything the company gives beyond it is added here instead. Zero
   * by default: a company that grants exactly the LFT table is the neutral case,
   * and a non-zero default would silently inflate everyone's entitlement.
   */
  readonly additionalDays: number;
  /** Brackets, ascending by `fromYear`. A tenure resolves to the highest
   *  bracket it reaches — 8 years matches the `fromYear: 6` bracket. */
  readonly brackets: readonly SeniorityBracket[];
}

export interface SeniorityBracket {
  readonly fromYear: number;
  readonly days: number;
}

/** The LFT table as of the 2023 reform, which is what the handoff ships. */
export const DEFAULT_SENIORITY_BRACKETS: readonly SeniorityBracket[] = [
  { fromYear: 1, days: 12 },
  { fromYear: 2, days: 14 },
  { fromYear: 3, days: 16 },
  { fromYear: 4, days: 18 },
  { fromYear: 5, days: 20 },
  { fromYear: 6, days: 22 },
  { fromYear: 11, days: 24 },
  { fromYear: 16, days: 26 },
  /* Los tramos de 21 años en adelante de la reforma de 2023, que faltaban: sin
     ellos, quien pasaba de veinte años se quedaba topado en 26 días. Agregados el
     21 sep 2026. Los directores con más días que la ley los tienen por contrato
     (`entitledDaysOverride`), no por esta tabla. */
  { fromYear: 21, days: 28 },
  { fromYear: 26, days: 30 },
  { fromYear: 31, days: 32 },
];

export const DEFAULT_RULES: RulesConfig = {
  legalMinimumDays: 12,
  noticeDays: 15,
  minimumRequestDays: 1,
  expiryMonths: 18,
  idleMinutes: 30,
  riskThresholdDays: 12,
  defaultMaxConcurrentOut: 2,
  // Los pisos de la LFT. Una empresa que dé más los sube en Configuración.
  aguinaldoDays: 15,
  primaVacacionalRate: 0.25,
  // $278.80 diarios, general 2026. Se revisa cada enero.
  minimumWageCents: 27_880,
  seniority: {
    enabled: true,
    fixedDays: 12,
    additionalDays: 0,
    brackets: DEFAULT_SENIORITY_BRACKETS,
  },
};

/** Editable catalogues — Configuración global › Catálogos. */
export interface CatalogsConfig {
  readonly incapacityKinds: readonly IncapacityKind[];
  readonly permissionKinds: readonly PermissionKind[];
  readonly rejectionReasons: readonly RejectionReason[];
  readonly positions: readonly Position[];
}

/**
 * Un puesto de trabajo.
 *
 * Catálogo y no texto libre, por lo mismo que las áreas: sobre 421 personas, un
 * campo abierto produce «Desarrollador iOS», «Dev iOS», «desarrollador ios» y
 * «Ingeniero de Software iOS» para el mismo puesto, y cualquier reporte que
 * agrupe por él deja de servir el día que alguien teclea distinto.
 *
 * Vive en `config/catalogs`, así que RR.HH. lo edita en vivo y un puesto nuevo no
 * necesita despliegue — la misma promesa OTA-safe de los tipos de permiso y los
 * motivos de rechazo.
 *
 * `enabled` en vez de borrar: un puesto que se deja de usar tiene gente con él
 * asignado, y borrarlo dejaría esas fichas apuntando a nada. Se apaga para que no
 * se ofrezca en altas nuevas y siga leyéndose en las viejas.
 */
export interface Position {
  readonly id: string;
  readonly title: string;
  readonly enabled: boolean;
}

/**
 * Los puestos con que arranca una instalación nueva.
 *
 * Deliberadamente genéricos y pocos: el catálogo real lo tiene RR.HH. y sale de
 * los contratos, no de lo que un programador imagine. Sembrar veinte puestos
 * inventados es lo que hizo el seeder con las áreas, y de ahí salieron cinco
 * áreas con sub áreas y correos de administrador que no existían.
 */
export const DEFAULT_POSITIONS: readonly Position[] = [
  { id: 'sin-asignar', title: 'Sin asignar', enabled: true },
];

/**
 * A selectable reason. Rejection and cancellation both demand one, which is what
 * makes the audit log useful: "Rechazada" with no reason tells a future reader
 * nothing.
 */
export interface RejectionReason {
  readonly id: string;
  readonly text: string;
  readonly scope: RejectionScope;
  readonly enabled: boolean;
}

export const DEFAULT_REJECTION_REASONS: readonly RejectionReason[] = [
  { id: 'vac-cierre', text: 'Coincide con el cierre de mes del área.', scope: 'vacaciones', enabled: true },
  {
    id: 'vac-tope',
    text: 'Ya está el máximo de personas fuera de la sub área en esas fechas.',
    scope: 'vacaciones',
    enabled: true,
  },
  {
    id: 'vac-reemplazo',
    text: 'El reemplazo designado no puede cubrir el periodo completo.',
    scope: 'vacaciones',
    enabled: true,
  },
  {
    id: 'per-presencia',
    text: 'El equipo necesita presencia en sitio esos días.',
    scope: 'permiso',
    enabled: true,
  },
  {
    id: 'per-entrega',
    text: 'Coincide con una entrega ya comprometida con el cliente.',
    scope: 'permiso',
    enabled: true,
  },
  {
    id: 'per-contexto',
    text: 'El motivo no da contexto suficiente para autorizar.',
    scope: 'permiso',
    enabled: true,
  },
  {
    id: 'inc-sello',
    text: 'El certificado no viene sellado por el IMSS.',
    scope: 'incapacidad',
    enabled: true,
  },
  {
    id: 'inc-folio',
    text: 'El folio capturado no coincide con el documento adjunto.',
    scope: 'incapacidad',
    enabled: true,
  },
  {
    id: 'inc-fechas',
    text: 'Faltan las fechas de reposo en el certificado.',
    scope: 'incapacidad',
    enabled: true,
  },
  {
    id: 'can-reagendar',
    text: 'Cambié de planes y quiero reagendar estos días.',
    scope: 'cancelacion',
    enabled: true,
  },
  {
    id: 'can-entrega',
    text: 'Surgió una entrega del proyecto en esas fechas.',
    scope: 'cancelacion',
    enabled: true,
  },
  {
    id: 'can-equipo',
    text: 'Prefiero mover los días para no dejar al equipo corto.',
    scope: 'cancelacion',
    enabled: true,
  },
  {
    id: 'can-personal',
    text: 'Motivo personal; los reagendo más adelante.',
    scope: 'cancelacion',
    enabled: true,
  },
];

/** Reasons offered for one scope, excluding the ones HR has switched off. */
export const reasonsForScope = (
  catalogs: Pick<CatalogsConfig, 'rejectionReasons'>,
  scope: RejectionScope,
): readonly RejectionReason[] =>
  catalogs.rejectionReasons.filter((r) => r.scope === scope && r.enabled);

/**
 * Lo mínimo que mide un motivo escrito a mano, y lo máximo.
 *
 * Diez caracteres: el piso que deja fuera lo que no es un motivo —«no», «x»,
 * «porque sí»— sin pretender medir si lo escrito es bueno. Es el espejo de
 * `FREE_REASON_MIN_LENGTH` en `functions/src/shared.ts`, que es quien de verdad
 * lo exige; si uno cambia y el otro no, el portal deja escribir algo que el
 * servidor va a devolver.
 */
export const FREE_REASON_MIN_LENGTH = 10;
export const REASON_MAX_LENGTH = 500;

/** Si un texto escrito a mano alcanza para ser un motivo. Los espacios no cuentan. */
export const isFreeReasonAcceptable = (text: string): boolean => {
  const length = text.trim().length;
  return length >= FREE_REASON_MIN_LENGTH && length <= REASON_MAX_LENGTH;
};

/**
 * Si un motivo vale para su ámbito: es uno de los que el catálogo ofrece ahí, o
 * es texto escrito que alcanza el mínimo.
 *
 * El catálogo sólo decide por debajo del mínimo: «Otro» es un motivo si alguien
 * lo dio de alta, y si no, no. Un texto largo vale por serlo, venga de donde
 * venga — también el de un motivo apagado o de otro ámbito, que escrito a mano
 * no se distingue de cualquier otro. Es la misma regla que `reasonAccepted` en
 * el servidor.
 */
export const isReasonAcceptable = (
  reason: string,
  offered: readonly Pick<RejectionReason, 'text'>[],
): boolean => offered.some((option) => option.text === reason) || isFreeReasonAcceptable(reason);

export const DEFAULT_CATALOGS_PLACEHOLDER_NOTE =
  'Los catálogos por omisión se siembran desde scripts/bootstrap-firestore.ts.';

// ---------------------------------------------------------------------------
// Email templates
// ---------------------------------------------------------------------------

/**
 * The notice catalogue, in the order the flow produces them.
 *
 * **Ten where the prototype has eight, and that is a marked divergence.** Two of the
 * eight were being used for notices they do not describe, which only became visible
 * once mail actually started arriving on 14 August:
 *
 *  - `recordatorio` reads «Tus vacaciones inician en 3 días», and the only thing
 *    sending it was the weekly reminder about days that are about to expire. The
 *    subject told about thirty people their holiday was starting when the message was
 *    that they were about to lose days. `vencimiento` is that notice; `recordatorio`
 *    goes back to being the one the prototype describes.
 *  - `decision` reads «ya tiene decisión de {{lider}}», and it was also sent when the
 *    *replacement* answered a designation — where there is no leader and no decision.
 *    `respaldo` is that one.
 *
 * Reusing a template whose subject contradicts its contents is worse than diverging
 * from a count: the person reading it cannot tell which notice they got.
 */
export const EMAIL_EVENTS = [
  'enviada',
  'lider',
  'reemplazo',
  'respaldo',
  'decision',
  'recordatorio',
  'vencimiento',
  'imss',
  'resumen',
  'datos',
  'reagenda',
  'permiso',
] as const;
export type EmailEvent = (typeof EMAIL_EVENTS)[number];

export const isEmailEvent = (value: unknown): value is EmailEvent =>
  typeof value === 'string' && (EMAIL_EVENTS as readonly string[]).includes(value);

/**
 * The variables an author may drop into a template. Substitution is a plain
 * whitelist replacement, never an expression evaluator: template bodies are
 * edited by users and rendered into email, so anything resembling code
 * execution would be a straightforward injection vector.
 */
export const EMAIL_VARIABLES = [
  'nombre',
  'folio',
  'periodo',
  'reemplazo',
  'lider',
  'area',
] as const;
export type EmailVariable = (typeof EMAIL_VARIABLES)[number];

export interface EmailTemplate {
  readonly event: EmailEvent;
  readonly enabled: boolean;
  readonly subject: string;
  readonly body: string;
  readonly ctaLabel: string;
  /** Who the notice goes to, shown in the list. Informational. */
  readonly audience: string;
  /** When it fires, e.g. "3 días antes". Informational. */
  readonly timing: string;
  readonly glyph: string;
  readonly color: string;
}

/**
 * A per-area override of one template — the handoff's "Propio del área". Keyed
 * `${areaId}_${event}` so an area can override some events and inherit the rest.
 */
export interface EmailTemplateOverride {
  readonly id: string;
  readonly areaId: AreaId;
  readonly event: EmailEvent;
  readonly subject: string;
  readonly body: string;
  readonly ctaLabel: string;
  readonly enabled: boolean;
}

export const emailOverrideId = (areaId: AreaId, event: EmailEvent): string =>
  `${areaId}_${event}`;

/**
 * The template that actually applies: the area's override when one exists,
 * otherwise the global template.
 */
export const resolveTemplate = (
  global: EmailTemplate,
  override: EmailTemplateOverride | undefined,
): EmailTemplate =>
  override === undefined
    ? global
    : {
        ...global,
        subject: override.subject,
        body: override.body,
        ctaLabel: override.ctaLabel,
        enabled: override.enabled,
      };

/**
 * Substitutes `{{variable}}` placeholders. Only the whitelisted names are
 * replaced; anything else is left as literal text rather than being cleared, so
 * a typo in a template shows up in the preview instead of vanishing silently.
 */
export const renderTemplate = (
  text: string,
  values: Readonly<Partial<Record<EmailVariable, string>>>,
): string =>
  text.replace(/\{\{\s*([a-zA-Z]+)\s*\}\}/g, (whole, name: string) => {
    if (!(EMAIL_VARIABLES as readonly string[]).includes(name)) return whole;
    return values[name as EmailVariable] ?? whole;
  });

// ---------------------------------------------------------------------------
// The aggregate
// ---------------------------------------------------------------------------

/** Everything the app reads live. Assembled from the `config/*` documents. */
export interface RuntimeConfig {
  readonly identity: IdentityConfig;
  readonly rules: RulesConfig;
  readonly catalogs: CatalogsConfig;
  readonly features: Readonly<Record<FeatureKey, boolean>>;
  readonly rolePermissions: Readonly<Record<Role, readonly Permission[]>>;
  readonly emailTemplates: Readonly<Record<EmailEvent, EmailTemplate>>;
  /**
   * Lo que cada área cambia sobre incapacidades y permisos, por `areaId`.
   *
   * Viaja con el resto de la configuración y no se consulta por separado porque
   * son ocho documentos diminutos y porque tiene que llegar por el mismo canal:
   * si el área apaga «Home office» a media mañana, el asistente de quien lo iba a
   * pedir deja de ofrecerlo sin recargar, igual que pasa con una bandera de
   * módulo. Un área sin documento no aparece aquí, y hereda el catálogo global.
   */
  readonly areaCatalogs: Readonly<Record<string, AreaCatalog>>;
  /**
   * Build version below which a client is asked to reload. The soft OTA gate:
   * it shows a banner, never a blank screen, because forcing a reload mid-form
   * would lose the user's work.
   */
  readonly minClientVersion: string;
  readonly maintenanceBanner: string | null;
}
